import { useState } from "react";
import { createDiscussion } from "wasp/client/operations";
import { useAction } from "wasp/client/operations";
import { AuthUser } from "wasp/auth";
import { routes } from "wasp/client/router";
import { useNavigate } from "react-router";
import { TextArea } from "../components/common";
import { Button, Input, LanguageSelect } from "../components/ui";
import { DISCUSSION_LANGUAGE_OPTIONS } from "./languageOptions";
import type { DiscussionLanguage } from "./types";

export function CreateDiscussionPage({ user }: { user: AuthUser }) {
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [tags, setTags] = useState<string[]>([]);
  const [language, setLanguage] = useState<DiscussionLanguage>("en");
  const [tagInput, setTagInput] = useState("");
  const [error, setError] = useState("");
  const navigate = useNavigate();

  const createDiscussionFn = useAction(createDiscussion);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    try {
      await createDiscussionFn({
        title,
        content,
        tags,
        language,
      });
      navigate(routes.DiscussionsRoute.to);
    } catch (err: any) {
      setError(err.message || "Error creating discussion");
    }
  };

  const handleAddTag = () => {
    if (tagInput && !tags.includes(tagInput)) {
      setTags([...tags, tagInput]);
      setTagInput("");
    }
  };

  const removeTag = (tagToRemove: string) => {
    setTags(tags.filter((tag) => tag !== tagToRemove));
  };

  return (
    <div className="min-h-screen bg-neutral-50 dark:bg-boxdark-2 py-12">
      <div className="max-w-2xl mx-auto bg-white dark:bg-boxdark rounded-xl shadow-xs p-8">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-neutral-900 dark:text-white">
            Create Discussion
          </h1>
          <p className="mt-2 text-neutral-600 dark:text-neutral-400">
            Share your thoughts with the community
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-8">
          {error && (
            <div className="bg-danger-50 dark:bg-danger-900/50 text-danger-500 dark:text-danger-300 p-4 rounded-lg">
              {error}
            </div>
          )}

          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <LanguageSelect
                label="Language"
                value={language}
                onChange={(value) => setLanguage(value as DiscussionLanguage)}
                options={DISCUSSION_LANGUAGE_OPTIONS}
              />
              <Input
                label="Title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                required
                placeholder="Enter discussion title"
              />
            </div>

            <TextArea
              label="Content"
              value={content}
              onChange={(e) => setContent(e.target.value)}
              rows={8}
              required
              placeholder="Share your thoughts..."
            />

            <div className="bg-neutral-50 dark:bg-neutral-800/20 rounded-lg p-4">
              <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-3">
                Tags
              </label>

              {tags.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-3">
                  {tags.map((tag) => (
                    <span
                      key={tag}
                      className="bg-primary-50 dark:bg-neutral-800 px-3 py-1.5 rounded-full text-sm flex items-center gap-2 text-primary dark:text-primary-400"
                    >
                      {tag}
                      <button
                        type="button"
                        onClick={() => removeTag(tag)}
                        className="text-primary/70 hover:text-primary dark:text-primary-400/70 dark:hover:text-primary transition-colors"
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              )}

              <div className="flex gap-3">
                <Input
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  placeholder="Add a tag"
                  className="flex-1"
                />
                <Button type="button" variant="primary" onClick={handleAddTag}>
                  Add Tag
                </Button>
              </div>
            </div>
          </div>

          <div className="pt-4">
            <Button type="submit" fullWidth>
              Create Discussion
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
